import type { Revision, RevisionTag, RevisionTags } from '../../types';
import { isYValue, jsonToY, mutations } from '../../utils';

/**
 * Get a tag in a revision by its slug.
 */
export function getTagInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string
): mutations.MutableData<RevisionTag> | undefined {
    const tags = mutations.get(revision, 'tags');
    if (!tags) {
        return undefined;
    }
    return mutations.get(tags, slug);
}

/**
 * Insert a tag in a revision (keyed by slug).
 */
export function insertTagInRevision(
    revision: mutations.MutableData<Revision>,
    tag: RevisionTag
): void {
    // Ensure tags map exists
    let tags = mutations.get(revision, 'tags');
    if (!tags) {
        tags = (isYValue(revision) ? jsonToY({}) : {}) as mutations.MutableData<RevisionTags>;
        mutations.set(revision, 'tags', tags);
    }

    const existing = mutations.get(tags, tag.slug);
    if (existing) {
        throw new Error('Tag with same slug already exists in revision');
    }
    mutations.set(tags, tag.slug, tag);
}

/**
 * Remove a tag from a revision by slug.
 */
export function removeTagInRevision(revision: mutations.MutableData<Revision>, slug: string): void {
    const tags = mutations.get(revision, 'tags');
    if (tags) {
        mutations.del(tags, slug);
    }
}

/**
 * Update a tag in a revision by slug.
 */
export function updateTagInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string,
    update: Partial<Omit<RevisionTag, 'slug'>>
): void {
    const existing = getTagInRevision(revision, slug);
    if (!existing) {
        throw new Error(`Tag doesn't exist in revision`);
    }
    Object.entries(update).forEach(([key, value]) => {
        mutations.set(
            existing,
            // @ts-ignore
            key,
            // @ts-ignore
            isYValue(revision) ? jsonToY(value) : value
        );
    });
}
